import { useState } from 'react'
import { PageContainer, ProTable } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { Alert, Button, type MenuProps, type TableProps } from 'antd'
import { ChevronDown, ListChecks, Plus } from 'lucide-react'
import { AuthorizedDropdown } from '@/features/authorization/authorized-dropdown.tsx'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { CustomerSendModal } from '@/features/customer-documents/index.ts'
import {
  createCustomerListColumns,
  CUSTOMER_BATCH_MENU_ITEMS,
  CustomerBatchTagsModal,
  CustomerMonthlyBillingModal,
  CustomerSearchBatchActions,
  customerSearchSchema,
  CustomerTagEditorModal,
  CustomerWecomBindingModal,
  DEFAULT_CUSTOMER_SEARCH,
  useCustomerListActions,
  type CustomerQuery,
  type CustomerRow,
  type CustomerSearchFormValues
} from '@/features/master-data/index.ts'
import { useMemberCreatorOptions, usePageTableNavigation } from '@/hooks/index.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useRemoteKeyword } from '@/hooks/use-remote-keyword.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'
import { getCreatedAtSearch, getCreatedAtSortOrder, getDateRangeFormValue } from '@/utils/list-query.ts'

const listQuery = createListQueryPersistence('super-mega:customer-list-query', customerSearchSchema)

export const Route = createFileRoute('/_authenticated/customer-management/customers/')({
  beforeLoad: (options) => {
    listQuery.restore(options)
  },
  component: CustomersPage,
  head: () => ({ meta: [{ title: '客户档案 · Super Mega' }] }),
  // 不在无参数导航时自动补齐默认值，保留“恢复记忆”和“显式默认查询”的区别。
  validateSearch: listQuery.validateSearch
})

function CustomersPage() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const trpc = useTRPC()
  const { currentSession } = Route.useRouteContext()
  const navigate = Route.useNavigate()
  const { search: query, navigateSearch } = usePersistedListQuery({
    persistence: listQuery,
    raw: Route.useSearch(),
    accountId: currentSession.id,
    navigate
  })
  const [creatingTag, setCreatingTag] = useState(false)
  const [bindingCustomer, setBindingCustomer] = useState<Pick<CustomerRow, 'id' | 'name'>>()
  const [tagCustomerIds, setTagCustomerIds] = useState<number[]>()
  const [billingIds, setBillingIds] = useState<number[]>()
  const [sendingCustomer, setSendingCustomer] = useState<{ id: number; name: string }>()
  const [sendOpen, setSendOpen] = useState(false)
  const canReadDocuments =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some(
      (permission) =>
        permission === 'order.rental.read' || permission === 'order.service.read' || permission === 'bill.read'
    )
  const canSubmitOrder = currentSession.isSuperAdmin || currentSession.permissions.includes('order.rental.create')
  const canManage =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some((permission) => permission.startsWith('customer.') && !permission.endsWith('.read'))
  const customers = useQuery({
    ...trpc.customer.list.queryOptions(query),
    placeholderData: keepPreviousData
  })
  const memberCreator = useMemberCreatorOptions('customer.read', query.creatorMemberId)
  const salespersonSearch = useRemoteKeyword()
  const salespeople = useQuery({
    ...trpc.lookup.members.queryOptions({ purpose: 'customer.read', keyword: salespersonSearch.keyword }),
    staleTime: 60_000
  })
  const createBindingGrant = useMutation(trpc.customer.createChatBindingGrant.mutationOptions())

  const {
    selectedCustomerIds,
    setSelectedCustomerIds,
    invalidateList,
    confirmChangeStatus,
    confirmRemoveCustomer,
    confirmChangeSelectedStatus,
    confirmRemoveSelectedCustomers,
    confirmClearSelectedTags,
    batchRemove,
    batchClearTags,
    batchSetDisabled
  } = useCustomerListActions({ query, rows: customers.data?.data, navigate: navigateSearch })
  const handleQueryChange = () => {
    setSelectedCustomerIds([])
  }
  const table = usePageTableNavigation<CustomerQuery, CustomerSearchFormValues>({
    defaultSearch: DEFAULT_CUSTOMER_SEARCH,
    getFormInitialValues: (search) => ({
      createdAtRange: getDateRangeFormValue(search.createdFrom, search.createdTo),
      creatorMemberId: search.creatorMemberId,
      keyword: search.keyword,
      noteKeyword: search.noteKeyword,
      tagIds: search.tagIds,
      salespersonId: search.salespersonId,
      wecomBindingStatus: search.wecomBindingStatus,
      collectionBillGroupingDay: search.collectionBillGroupingDay,
      id: search.id,
      status: search.status
    }),
    getSearchFromForm: (values) => ({
      ...getCreatedAtSearch(values.createdAtRange),
      creatorMemberId: values.creatorMemberId,
      keyword: values.keyword?.trim() || undefined,
      noteKeyword: values.noteKeyword?.trim() || undefined,
      tagIds: values.tagIds?.length ? values.tagIds : undefined,
      salespersonId: values.salespersonId,
      wecomBindingStatus: values.wecomBindingStatus,
      collectionBillGroupingDay: values.collectionBillGroupingDay,
      id: values.id,
      status: values.status
    }),
    navigate: navigateSearch,
    onQueryChange: handleQueryChange,
    search: query,
    showTotal: (total) => `共 ${total} 位客户`
  })

  const handleTableChange: NonNullable<TableProps<CustomerRow>['onChange']> =
    /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
    (_pagination, _filters, sorter, extra) => {
      if (extra.action !== 'sort') {
        return
      }
      const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter
      const sortOrder = getCreatedAtSortOrder(currentSorter.order)
      handleQueryChange()
      void navigate({
        replace: true,
        search: (previous) => ({
          ...previous,
          current: 1,
          sortField: 'createdAt',
          sortOrder
        })
      })
    }
  /* eslint-enable max-params */

  const batchMenu: MenuProps = {
    items: CUSTOMER_BATCH_MENU_ITEMS,
    onClick: ({ key }) => {
      if (key === 'clear-tags') {
        confirmClearSelectedTags()
        return
      }
      if (key === 'add-tags') {
        setTagCustomerIds([...selectedCustomerIds])
        return
      }
      if (key === 'monthly-billing') {
        setBillingIds([...selectedCustomerIds])
        return
      }
      if (key === 'delete') {
        confirmRemoveSelectedCustomers()
        return
      }
      confirmChangeSelectedStatus(key === 'disable')
    }
  }

  const salespersonOptions = (salespeople.data ?? []).map((person) => ({
    label: person.label,
    value: person.value
  }))
  const columns = createCustomerListColumns({
    query,
    memberCreator,
    salespersonOptions,
    salespeopleLoading: salespeople.isFetching,
    onSalespersonSearch: salespersonSearch.onSearch,
    canReadDocuments,
    canSubmitOrder,
    canManage,
    onEdit: (customer: CustomerRow) =>
      void navigate({
        to: '/customer-management/customers/$customerId/edit',
        params: { customerId: String(customer.id) },
        search: {},
        state: { customerListReturnSearch: query }
      }),
    onView: (customer: CustomerRow) =>
      void navigate({
        to: '/customer-management/customers/$customerId',
        params: { customerId: String(customer.id) },
        search: query
      }),
    onDocuments: (customer: CustomerRow) => {
      window.open(
        router.buildLocation({
          to: '/customer-management/customers/$customerId/documents',
          params: { customerId: String(customer.id) },
          search: {}
        }).href,
        '_blank',
        'noopener,noreferrer'
      )
    },
    onCreateRental: (customer: CustomerRow) => {
      if (customer.isDisable) {
        return
      }
      void navigate({
        to: '/orders/rentals/new',
        search: { customerId: customer.id }
      })
    },
    onCreateService: (customer: CustomerRow) => {
      if (customer.isDisable) {
        return
      }
      void navigate({
        to: '/orders/service/new',
        search: { customerId: customer.id }
      })
    },
    onBindWecom: (customer: CustomerRow) => {
      setBindingCustomer({ id: customer.id, name: customer.name })
      createBindingGrant.mutate({ customerId: customer.id })
    },
    onSend: (customer: CustomerRow) => {
      setSendingCustomer({ id: customer.id, name: customer.name })
      setSendOpen(true)
    },
    onChangeStatus: (customer: CustomerRow) => confirmChangeStatus(customer),
    onRemove: (customer: CustomerRow) => confirmRemoveCustomer(customer)
  })

  return (
    <PageContainer
      breadcrumbRender={false}
      content="维护客户联系方式、企业信息、客户标签、业务员与业务附件。"
      title="客户档案"
    >
      {customers.isError ? (
        <Alert
          action={
            <Button onClick={() => void customers.refetch()} size="small">
              重试
            </Button>
          }
          className="mb-4"
          showIcon
          title={getErrorMessage(customers.error, '客户列表加载失败')}
          type="error"
        />
      ) : null}
      <ProTable<CustomerRow, CustomerSearchFormValues>
        columns={columns}
        columnsState={{ persistenceKey: 'customer-management-customers:table', persistenceType: 'localStorage' }}
        dataSource={customers.data?.data ?? []}
        onChange={handleTableChange}
        loading={{ delay: 150, spinning: customers.isFetching }}
        onReset={table.onReset}
        onSubmit={table.onSubmit}
        options={{ reload: () => void customers.refetch() }}
        pagination={{ ...table.pagination, total: customers.data?.total ?? 0 }}
        formRef={table.formRef}
        rowKey="id"
        rowSelection={
          canManage
            ? {
                onChange: (keys) => setSelectedCustomerIds(keys.map(Number)),
                selectedRowKeys: selectedCustomerIds
              }
            : undefined
        }
        scroll={{ x: '137rem' }}
        form={{ initialValues: table.formInitialValues }}
        search={{ defaultCollapsed: false, labelWidth: 'auto' }}
        size="small"
        toolBarRender={() =>
          canManage
            ? [
                <CustomerSearchBatchActions
                  key="search-batch-actions"
                  search={query}
                  total={customers.data?.total ?? 0}
                  disabled={
                    customers.isFetching ||
                    customers.isError ||
                    batchRemove.isPending ||
                    batchClearTags.isPending ||
                    batchSetDisabled.isPending
                  }
                  onSaved={() => {
                    setSelectedCustomerIds([])
                    if (query.current > 1) {
                      void navigate({ search: (previous) => ({ ...previous, current: 1 }) })
                    }
                  }}
                />,
                <AuthorizedDropdown
                  resource="customer"
                  key="batch-actions"
                  menu={batchMenu}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button
                    disabled={selectedCustomerIds.length === 0 || batchRemove.isPending || batchClearTags.isPending}
                    icon={<ListChecks aria-hidden size={16} />}
                  >
                    批量操作 <ChevronDown aria-hidden size={14} />
                  </Button>
                </AuthorizedDropdown>,
                <Authorized key="new-tag" permissions={['customer.create']}>
                  <Button icon={<Plus aria-hidden size={16} />} key="new-tag" onClick={() => setCreatingTag(true)}>
                    新建客户标签
                  </Button>
                </Authorized>,
                <Authorized key="new" permissions={['customer.create']}>
                  <Button
                    icon={<Plus aria-hidden size={16} />}
                    key="new"
                    onClick={() => {
                      void navigate({
                        to: '/customer-management/customers/new',
                        search: {},
                        state: { customerListReturnSearch: query }
                      })
                    }}
                    type="primary"
                  >
                    新建客户
                  </Button>
                </Authorized>
              ]
            : []
        }
      />

      <CustomerTagEditorModal
        editing={creatingTag ? 'new' : undefined}
        onClose={() => setCreatingTag(false)}
        onSaved={async () => {
          setCreatingTag(false)
          await Promise.all([
            queryClient.invalidateQueries(trpc.customer.tagOptions.queryFilter()),
            queryClient.invalidateQueries(trpc.customer.tagList.queryFilter())
          ])
        }}
      />
      {sendingCustomer && (
        <CustomerSendModal
          customerId={sendingCustomer.id}
          customerName={sendingCustomer.name}
          open={sendOpen}
          onClose={() => setSendOpen(false)}
          afterClose={() => setSendingCustomer(undefined)}
        />
      )}
      {bindingCustomer ? (
        <CustomerWecomBindingModal
          customer={bindingCustomer}
          grant={createBindingGrant.data}
          loading={createBindingGrant.isPending}
          error={createBindingGrant.error}
          onGenerate={() => createBindingGrant.mutate({ customerId: bindingCustomer.id })}
          onClose={() => {
            setBindingCustomer(undefined)
            void invalidateList()
          }}
        />
      ) : null}
      {tagCustomerIds ? (
        <CustomerBatchTagsModal
          ids={tagCustomerIds}
          onClose={() => setTagCustomerIds(undefined)}
          onSaved={() => {
            setTagCustomerIds(undefined)
            setSelectedCustomerIds([])
          }}
        />
      ) : null}
      {billingIds ? (
        <CustomerMonthlyBillingModal
          ids={billingIds}
          onClose={() => setBillingIds(undefined)}
          onSaved={() => {
            setBillingIds(undefined)
            setSelectedCustomerIds([])
          }}
        />
      ) : null}
    </PageContainer>
  )
}
